import {
  INITIAL_SECONDARY_USERS_VISIBLE,
  STORE_RELATIONS_ACTION_LABELS,
  STORE_RELATIONS_STATUS_UI_MAP
} from '../constants/storeConstants'
import {
  canStoreRelationAssignPrincipal,
  hasStoreRelationAssignedPrincipal,
  hasStoreRelationPrincipalConflict,
  isStoreRelationClientInactive,
  resolveStoreRelationOverallStatus,
  resolveStoreRelationSecondaryStatus
} from './storeRelationStatusUtils'
import {
  dedupeStoreRelationUsersBySalesmanAndStatus,
  formatStoreRelationClientLabel,
  formatStoreRelationName,
  formatStoreRelationSalesmanLabel,
  getStoreRelationModelKey
} from '../utils/storeRelationDisplayUtils'

export const resolveStoreRelationRowState = (model) => {
  const rowId = getStoreRelationModelKey(model, 'store-relation')
  const supplierLabel = formatStoreRelationName(
    model?.client_configuration?.supplier_name || model?.client_principal?.supplier_name
  )
  const buyerLabel = formatStoreRelationName(
    model?.client_configuration?.buyer_name || model?.client_principal?.buyer_name
  )
  const storeRef = formatStoreRelationName(
    model?.client_configuration?.store_reference || model?.client_principal?.store_reference
  )
  const clientLabel = formatStoreRelationClientLabel(model?.client)
  const principalRelationId = model?.client_principal?.id
  const hasPrincipalConflict = hasStoreRelationPrincipalConflict(model)
  const principalRelations = (
    hasPrincipalConflict
      ? dedupeStoreRelationUsersBySalesmanAndStatus(model?.principal_candidates || [])
      : [model?.client_principal]
  ).filter((relation) => Boolean(relation?.salesman))
  const secondaryUsers = dedupeStoreRelationUsersBySalesmanAndStatus(model?.client_brands || [])
  const overallStatus = resolveStoreRelationOverallStatus(model)
  const statusUi = STORE_RELATIONS_STATUS_UI_MAP[overallStatus] || null
  const isClientInactive = isStoreRelationClientInactive(model)
  const hasAssignedPrincipalUser = hasStoreRelationAssignedPrincipal(model)
  const hasReassignContext = Boolean(
    model?.client_configuration?.id || model?.client?.id || model?.client?.db_ref
  )
  const canAssignPrincipal = !isClientInactive && canStoreRelationAssignPrincipal(model)
  const canManageRelation =
    !isClientInactive &&
    Boolean(principalRelationId) &&
    hasAssignedPrincipalUser &&
    !hasPrincipalConflict
  const canEditRelation = canManageRelation && ['active', 'error'].includes(overallStatus)
  const canReassignPrincipal = !isClientInactive && hasReassignContext && hasPrincipalConflict
  const canSuspendRelation = canManageRelation && ['active', 'error'].includes(overallStatus)
  const canReactivateRelation = canManageRelation && overallStatus === 'suspended'
  const initialSecondaryUsers = secondaryUsers.slice(0, INITIAL_SECONDARY_USERS_VISIBLE)
  const extraSecondaryUsers = secondaryUsers.slice(INITIAL_SECONDARY_USERS_VISIBLE)

  return {
    rowId,
    supplierLabel,
    buyerLabel,
    clientLabel,
    storeRef,
    principalRelations,
    secondaryUsers,
    initialSecondaryUsers,
    extraSecondaryUsers,
    hasHiddenSecondaryUsers: extraSecondaryUsers.length > 0,
    hasPrincipalConflict,
    overallStatus,
    statusUi,
    canAssignPrincipal,
    canReassignPrincipal,
    canEditRelation,
    canSuspendRelation,
    canReactivateRelation
  }
}

export const buildStoreRelationRowActions = ({
  model,
  canAssignPrincipal,
  canReassignPrincipal,
  canEditRelation,
  canSuspendRelation,
  canReactivateRelation,
  onAssignPrincipal,
  onReassignPrincipal,
  onEdit,
  onSuspend,
  onReactivate
}) => {
  return [
    canAssignPrincipal
      ? {
          text: STORE_RELATIONS_ACTION_LABELS.ASSIGN_USER,
          handler: () => onAssignPrincipal?.(model)
        }
      : null,
    canReassignPrincipal
      ? {
          text: STORE_RELATIONS_ACTION_LABELS.REASSIGN_USER,
          handler: () => onReassignPrincipal?.(model)
        }
      : null,
    canEditRelation
      ? {
          text: STORE_RELATIONS_ACTION_LABELS.EDIT,
          handler: () => onEdit?.(model)
        }
      : null,
    canSuspendRelation
      ? {
          text: STORE_RELATIONS_ACTION_LABELS.SUSPEND,
          handler: () => onSuspend?.(model)
        }
      : null,
    canReactivateRelation
      ? {
          text: STORE_RELATIONS_ACTION_LABELS.REACTIVATE,
          handler: () => onReactivate?.(model)
        }
      : null
  ].filter(Boolean)
}

export const getStoreRelationUserChipKey = ({ relation, index, suffix, status }) => {
  return `${relation?.salesman?.id || relation?.salesman?.db_ref || index}-${status}-${suffix}`
}

export const getStoreRelationUserChipLabel = (relation) => {
  return formatStoreRelationSalesmanLabel(relation?.salesman).toUpperCase()
}

export const getStoreRelationUserChipStatus = (relation, hasPrincipalConflict = false) => {
  if (hasPrincipalConflict) return 'error'

  return resolveStoreRelationSecondaryStatus(relation)
}
